/**
 * Dashboard – role router.
 *
 * One route, three pages. Which one renders is decided by the signed-in
 * user's highest-privilege role (lib/currentUser.jsx):
 *
 *   SUPER_ADMIN, DEALER_PRINCIPAL, READ_ONLY_AUDITOR → Dealer Principal
 *   SALES_MANAGER                                    → Sales Manager
 *   SALES_CONSULTANT, BDC_AGENT                      → Sales Consultant (My Day)
 *   SERVICE_MANAGER, SERVICE_ADVISOR                 → redirected to /service
 *
 * The service redirect is deliberate: sales and service are segregated, and a
 * sales dashboard must never be a service user's landing page.
 *
 * Each user sees only the dashboard for their own role. The "View as"
 * switcher is limited to SUPER_ADMIN (PMDG operations), plus local dev
 * builds running in design mode.
 *
 * `?view=legacy` renders the pre-split dashboard for side-by-side comparison.
 */

import { useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import TopBar from '../components/TopBar';
import MultiSelectFilter from '../components/MultiSelectFilter';
import { useCurrentUser, setDevRole } from '../lib/currentUser';
import { roleLabel } from '../lib/roles';
import { dashboardService, locationsService } from '../lib/services';
import { useRoleSnapshot, PERIODS, periodLabel } from '../components/dashboard/useRoleSnapshot';
import DealerPrincipalDashboard from './dashboards/DealerPrincipalDashboard';
import SalesManagerDashboard from './dashboards/SalesManagerDashboard';
import SalesConsultantDashboard from './dashboards/SalesConsultantDashboard';
import InventoryControllerDashboard from './dashboards/InventoryControllerDashboard';
import FleetConsultantDashboard from './dashboards/FleetConsultantDashboard';
import BDCAgentDashboard from './dashboards/BDCAgentDashboard';
import TestDriveControllerDashboard from './dashboards/TestDriveControllerDashboard';
import LegacyDashboard from './dashboards/LegacyDashboard';
import ExecutiveDashboard from './dashboards/ExecutiveDashboard';
import ConsultantHomeDashboard from './dashboards/ConsultantHomeDashboard';
import { useSnapshotCapture } from '../components/dashboard/useSnapshotCapture';

const VIEW_FOR_ROLE = {
  SUPER_ADMIN:       'DEALER_PRINCIPAL',
  DEALER_PRINCIPAL:  'DEALER_PRINCIPAL',
  GM_SALES:          'DEALER_PRINCIPAL',
  CEO:               'DEALER_PRINCIPAL',
  READ_ONLY_AUDITOR: 'DEALER_PRINCIPAL',
  SALES_MANAGER:     'SALES_MANAGER',
  USED_CAR_MANAGER:  'SALES_MANAGER',
  // Three role-shaped landing pages (2026-10-01, closing the matrix's
  // Reports column): Inventory Controller → stock-only, Fleet Consultant →
  // personal + Deal Desk extras, BDC Agent → queue + first-response SLA.
  // Each is a view inside this same Dashboard page, not a new nav item.
  INVENTORY_MANAGER:     'INVENTORY_CONTROLLER',
  FLEET_MANAGER:         'FLEET_CONSULTANT',
  BDC_AGENT:             'BDC_AGENT',
  TEST_DRIVE_CONTROLLER: 'TEST_DRIVE_CONTROLLER',
  SALES_CONSULTANT:  'SALES_CONSULTANT',
  NEW_CAR_SALES_CONSULTANT:  'SALES_CONSULTANT',
  USED_CAR_SALES_CONSULTANT: 'SALES_CONSULTANT',
};

const SWITCHABLE = ['DEALER_PRINCIPAL', 'SALES_MANAGER', 'SALES_CONSULTANT'];
const SERVICE_ROLES = new Set(['SERVICE_MANAGER', 'SERVICE_ADVISOR']);
const PERIOD_KEY = 'spectraiq.dashboard.period';

export default function Dashboard() {
  const { user, role, loading: userLoading, consultantId, canSwitchRole, designOnly } = useCurrentUser();
  const [params] = useSearchParams();

  const [asView, setAsView] = useState(null);
  const [period, setPeriod] = useState(() => {
    try { return window.localStorage.getItem(PERIOD_KEY) || '7d'; } catch { return '7d'; }
  });
  useEffect(() => { try { window.localStorage.setItem(PERIOD_KEY, period); } catch { /* ignore */ } }, [period]);

  const ownView = VIEW_FOR_ROLE[role] || 'SALES_CONSULTANT';
  // Design mode (no backend/token) is a local-dev affordance only – never in a
  // production build, where a failed /users/me must not unlock the switcher.
  const devSwitch = designOnly && import.meta.env.DEV;
  const view = ((canSwitchRole || devSwitch) && asView) || ownView;

  /**
   * The next-longer period, for the funnel's empty state to offer.
   *
   * A dealership can have a page full of 100-day-old open leads and a funnel
   * of zeroes, because the funnel counts movement inside the window and the
   * window is seven days. That is correct, and it reads as broken. The fix is
   * for the empty funnel to say what it means and hand over the one control
   * that changes the answer, rather than leaving the user to find the period
   * toggle in the top bar and guess that it is related.
   */
  const widen = useMemo(() => {
    const i = PERIODS.findIndex((p) => p.id === period);
    const next = i >= 0 ? PERIODS[i + 1] : null;
    return next ? { label: next.label, apply: () => setPeriod(next.id) } : null;
  }, [period]);

  // T079 – multi-location filter. GM/group-level concern, so it only shows
  // (and only fetches) on the Dealer Principal view; a single-rooftop
  // dealership never sees it, since `locations` stays empty/absent then.
  const [locations, setLocations] = useState([]);
  const [locationIds, setLocationIds] = useState(() => new Set());
  useEffect(() => {
    if (view !== 'DEALER_PRINCIPAL') return;
    let cancelled = false;
    locationsService.list()
      .then((rows) => { if (!cancelled) setLocations((Array.isArray(rows) ? rows : []).filter((l) => l.active !== false)); })
      .catch(() => { if (!cancelled) setLocations([]); });
    return () => { cancelled = true; };
  }, [view]);

  // Sparkline series – the only piece of the old dashboard the new ones reuse.
  const [series, setSeries] = useState({ leads: [] });
  useEffect(() => {
    let cancelled = false;
    const days = Math.max(7, PERIODS.find((p) => p.id === period)?.days ?? 7);
    dashboardService.series({ metric: 'leads', days })
      .then((arr) => { if (!cancelled) setSeries({ leads: Array.isArray(arr) ? arr.map((p) => Number(p.value) || 0) : [] }); })
      .catch(() => { if (!cancelled) setSeries({ leads: [] }); });
    return () => { cancelled = true; };
  }, [period]);

  const { snapshot, loading, error, refetch } = useRoleSnapshot({
    role: view,
    period,
    consultantId: view === 'SALES_CONSULTANT' ? consultantId : null,
    locationIds: view === 'DEALER_PRINCIPAL' ? locationIds : null,
  });

  // Snapshot (PNG of the page) for the modern homes; the legacy page has its own.
  const shot = useSnapshotCapture();

  const title = useMemo(() => ({
    DEALER_PRINCIPAL:      'Dealership',
    SALES_MANAGER:         'Sales team',
    SALES_CONSULTANT:      'My Day',
    INVENTORY_CONTROLLER:  'Stock Reports',
    FLEET_CONSULTANT:      'My Fleet Desk',
    BDC_AGENT:             'BDC Board',
    TEST_DRIVE_CONTROLLER: 'Demo Fleet',
  })[view], [view]);

  // Inventory Controller lands on a stock-only view — a separate endpoint,
  // not role-snapshot. Only fetched when that view is active so the other
  // dashboards don't pay the cost (or 403 if the caller lacks INVENTORY_WRITE).
  const [inventory, setInventory] = useState(null);
  useEffect(() => {
    if (view !== 'INVENTORY_CONTROLLER') return;
    let cancelled = false;
    dashboardService.inventorySnapshot()
      .then((data) => { if (!cancelled) setInventory(data); })
      .catch(() => { if (!cancelled) setInventory(null); });
    return () => { cancelled = true; };
  }, [view]);

  // Restored 2026-09-17 – the pre-split single-page dashboard is the
  // default home for the legacy sales roles. The role-split dashboards
  // (Dealer Principal / Sales Manager / Sales Consultant) stay reachable
  // via `?view=split` for anyone who wants them.
  //
  // 2026-10-01 — the three roles added this round (Inventory Controller,
  // Fleet Consultant, BDC Agent) always land on their own specialised view
  // regardless of the `?view=split` flag. For them the legacy home is
  // irrelevant (it is a sales-funnel/hot-leads screen and they don't own
  // those), so we skip it. The flag only still gates the three legacy
  // sales roles.
  const alwaysSpecialised =
    view === 'INVENTORY_CONTROLLER' ||
    view === 'FLEET_CONSULTANT' ||
    view === 'BDC_AGENT' ||
    view === 'TEST_DRIVE_CONTROLLER';
  // 2026-10-29 — the modern Executive (Dealer Principal / GM) and Consultant
  // homes are the default for those views. `?view=legacy` still opens the
  // pre-split page and `?view=split` the previous role dashboards; Sales
  // Managers keep the legacy home until their redesign.
  const viewParam = params.get('view');
  const modern = (view === 'DEALER_PRINCIPAL' || view === 'SALES_CONSULTANT')
    && viewParam !== 'split' && viewParam !== 'legacy';
  if (viewParam === 'legacy') return <LegacyDashboard />;
  if (!alwaysSpecialised && !modern && viewParam !== 'split') return <LegacyDashboard />;

  // Segregation: a service user never lands on the sales dashboard.
  if (!userLoading && SERVICE_ROLES.has(role)) return <Navigate to="/service" replace />;

  if (userLoading && !user) {
    return (
      <>
        <TopBar crumbs={['Home']} />
        <div className="app-content">
          <div className="grid-5">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="kpi-tile" style={{ minHeight: 96, opacity: 0.4 }} />)}</div>
        </div>
      </>
    );
  }

  return (
    <>
      <TopBar
        crumbs={['Home', title]}
        right={
          <>
            {(canSwitchRole || devSwitch) && !modern && (
              <div className="mode-toggle" role="tablist" aria-label="View as">
                {SWITCHABLE.map((v) => (
                  <button
                    key={v}
                    role="tab"
                    aria-selected={view === v}
                    className={view === v ? 'active' : ''}
                    onClick={() => {
                      setAsView(v === ownView ? null : v);
                      if (designOnly) setDevRole(v);
                    }}
                    title={designOnly ? 'Design mode – sets the dev role' : `View as ${roleLabel(v)}`}
                  >
                    {roleLabel(v)}
                  </button>
                ))}
              </div>
            )}
            {view === 'DEALER_PRINCIPAL' && locations.length > 1 && (
              <MultiSelectFilter
                label="SITE"
                options={locations.map((l) => ({ value: l.id, label: l.name }))}
                value={locationIds}
                onChange={setLocationIds}
              />
            )}
            {(view !== 'SALES_CONSULTANT' || modern) && (
              <div className="period-toggle" role="tablist" aria-label="Period">
                {PERIODS.map((p) => (
                  <button
                    key={p.id}
                    role="tab"
                    aria-selected={period === p.id}
                    onClick={() => setPeriod(p.id)}
                    className={period === p.id ? 'active' : ''}
                  >{p.label}</button>
                ))}
              </div>
            )}
            {modern && (
              <button className="btn small" onClick={shot.capture} disabled={shot.snapping}
                title="Capture the dashboard as an image — copied to your clipboard (paste into a deck/email/chat) and downloaded as a PNG">
                {shot.snapping ? 'Capturing…' : '📸 Snapshot'}
              </button>
            )}
            <button className="btn small" onClick={refetch} title="Refresh">↻</button>
          </>
        }
      />

      {/* Errors render. The old page captured them into state and showed
          stale zeros with no indication anything was wrong. */}
      {error && !loading && (
        <div className="app-content" style={{ paddingBottom: 0 }}>
          <div className="card" style={{ borderColor: 'var(--danger)', padding: 12 }}>
            <div className="flex-between">
              <span style={{ color: 'var(--danger)', fontSize: 13 }}>
                Couldn't load the dashboard{snapshot ? ' – showing the last good numbers' : ''}.
              </span>
              <button className="btn small" onClick={refetch}>Retry</button>
            </div>
          </div>
        </div>
      )}

      {loading && !snapshot ? (
        <div className="app-content">
          <div className="grid-5">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="kpi-tile" style={{ minHeight: 96, opacity: 0.4 }} />)}</div>
          <div className="mono text-xs text-muted" style={{ marginTop: 12 }}>loading…</div>
        </div>
      ) : view === 'SALES_CONSULTANT' ? (
        modern
          ? <ConsultantHomeDashboard contentRef={shot.ref} snapshot={snapshot} period={period} periodLabel={periodLabel(period)} />
          : <SalesConsultantDashboard snapshot={snapshot} periodLabel={periodLabel(period)} />
      ) : view === 'SALES_MANAGER' ? (
        <SalesManagerDashboard snapshot={snapshot} periodLabel={periodLabel(period)} onChanged={refetch} widen={widen} />
      ) : view === 'INVENTORY_CONTROLLER' ? (
        <InventoryControllerDashboard inventory={inventory} />
      ) : view === 'FLEET_CONSULTANT' ? (
        <FleetConsultantDashboard snapshot={snapshot} periodLabel={periodLabel(period)} />
      ) : view === 'BDC_AGENT' ? (
        <BDCAgentDashboard snapshot={snapshot} periodLabel={periodLabel(period)} />
      ) : view === 'TEST_DRIVE_CONTROLLER' ? (
        <TestDriveControllerDashboard snapshot={snapshot} />
      ) : (
        modern
          ? <ExecutiveDashboard contentRef={shot.ref} snapshot={snapshot} period={period} periodLabel={periodLabel(period)} />
          : <DealerPrincipalDashboard snapshot={snapshot} periodLabel={periodLabel(period)} series={series} />
      )}
    </>
  );
}
